<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentStatus: { type: String, default: 'open' },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();
const { updateUISettings } = useUISettings();
const chatSortFilter = useMapGetter('getChatSortFilter');

const [showDropdown, toggleDropdown] = useToggle();

const STATUSES = ['open', 'resolved', 'pending', 'snoozed', 'all'];

const options = computed(() =>
  STATUSES.map(value => ({
    value,
    label: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${value}.TEXT`),
  }))
);

const activeLabel = computed(() =>
  t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${props.currentStatus}.TEXT`)
);

const selectStatus = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  updateUISettings({
    conversations_filter_by: {
      status: value,
      order_by:
        chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
    },
  });
  toggleDropdown(false);
};
</script>

<template>
  <div class="relative shrink-0">
    <button class="status-pill" @click="toggleDropdown()">
      <span class="truncate">{{ activeLabel }}</span>
      <span class="i-lucide-chevron-down size-3.5 shrink-0" />
    </button>
    <div
      v-if="showDropdown"
      v-on-click-outside="() => toggleDropdown(false)"
      class="absolute z-40 mt-1 top-full w-56 p-1.5 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg"
      :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
    >
      <button
        v-for="option in options"
        :key="option.value"
        class="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-2"
        @click="selectStatus(option.value)"
      >
        <span
          class="grid border rounded place-content-center size-4 shrink-0"
          :class="
            option.value === currentStatus
              ? 'bg-n-brand border-n-brand text-white'
              : 'border-n-strong'
          "
        >
          <span
            v-if="option.value === currentStatus"
            class="i-lucide-check size-3"
          />
        </span>
        <span class="truncate">{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.status-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #6f7584;
  background: #eceff5;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  &:hover {
    color: #181f30;
  }
}
</style>
